import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Row, Col, Card, Form } from 'react-bootstrap';
import { DeleteJson } from '@awp/helpers/fetchReact';
import { ToastContainer, toast } from 'react-toastify';
import AwpIsExpanded from '@components/Common/AwpIsExpanded';
import TemplateObservationTypeInfo from './TemplateObservationTypeInfo';
import TemplateObservationTypeEdit from './TemplateObservationTypeEdit';
import {
  isJsonValueEmpty,
  isInt,
  isValueEmpty,
  isValidRelElevation,
  isValidRelElevationDigit,
} from '@awp/helpers/utilsReact';
import Authorize from '@awp/components/Authorize';
import {
  REST_API,
  SENSOR_PLACEMENT_CONFIG,
  FROST_PROBE_CONFIG,
  SNOW_POLE_CONFIG,
  Permissions,
  STATE_STATUS,
} from '@awp/constants';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import {
  isEditing,
  setEditing,
  setTemplate,
  setOrignTemplate,
  saveTemplate,
  getCurrentPlacementTemplatePacket,
  getSelectedTemplate,
  getOrignTemplate,
  getSaveTemplateStatus,
  getSaveTemplateError,
  setSaveTemplateStatus,
  setSaveTemplateError,
  setData,
} from '@stores/placementTemplatePacketSlice';

const txtFontSize = {
  fontSize: '90%',
  paddingTop: '0.45rem',
  paddingBottom: '0.45rem',
};

const btnStyle = {
  marginRight: '1rem',
  fontSize: '75%',
};
const btnDelStyle = {
  fontSize: '85%',
};
const sotTitleStyle = {
  paddingTop: '0.6rem',
  paddingBottom: '0.6rem',
  display: 'flex',
  alignItems: 'center',
};
const sotOutline = {
  outline: '0.063rem solid black',
  padding: '0rem 1rem',
  marginTop: '0.5rem',
  outlineOffset: '-0.063rem',
};

const tplNamePrefix = 'placementtemplate.name.';
const totOtIdPrefix = 'templateobservationtype.observationtypeid.';
const totOffsetelevationmillimetresPrefix = 'templateobservationtype.offsetelevationmillimetres.';

const PlacementTemplate = ({ template }) => {
  const dispatch = useDispatch();
  const inEditing = useSelector(isEditing);
  const currentPlacementTemplatePacket = useSelector(getCurrentPlacementTemplatePacket);
  const selectedTemplate = useSelector(getSelectedTemplate);
  const selectedOrignTemplate = useSelector(getOrignTemplate);
  const isSaveStatus = useSelector(getSaveTemplateStatus);
  const saveTemplateError = useSelector(getSaveTemplateError);
  const [showDeleteTemplateConfirm, setShowDeleteTemplateConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const isExpanded = () => {
    setIsCollapsed(!isCollapsed);
  };

  const isOtherEditing = () => {
    return !isValueEmpty(inEditing) && inEditing !== SENSOR_PLACEMENT_CONFIG.TEMPLATE_TAG + template.Id;
  };
  const isCurrentEditing = () => {
    return !isValueEmpty(inEditing) && inEditing === SENSOR_PLACEMENT_CONFIG.TEMPLATE_TAG + template.Id;
  };
  const enableEdit = () => {
    if (isOtherEditing() || isCurrentEditing()) return;
    dispatch(setEditing(SENSOR_PLACEMENT_CONFIG.TEMPLATE_TAG + template.Id));
    let _template = { ...template };
    dispatch(setOrignTemplate(_template));
    let _template2 = { ..._template };
    if (isJsonValueEmpty(template.TemplateObservationTypes) || template.TemplateObservationTypes.length === 0) {
      let newTot = { ...SENSOR_PLACEMENT_CONFIG.NEW_TEMPLATE_OBSERVATION_TYPE };
      newTot.Id = SENSOR_PLACEMENT_CONFIG.TOT_ID - 1;
      newTot.PlacementTemplateId = template.Id;
      let newTots = [];
      newTots.push(newTot);
      _template2.TemplateObservationTypes = newTots;
    }

    dispatch(setTemplate(_template2));
    setIsCollapsed(false);
  };
  const updateTemplateName = (e) => {
    e.preventDefault();

    let _selectedTemplate = { ...selectedTemplate };
    _selectedTemplate.Name = e.target.value;
    dispatch(setTemplate(_selectedTemplate));
  };
  const switchViewMode = () => {
    cleanData();
    setShowCancelConfirm(false);
    setShowDeleteTemplateConfirm(false);
  };
  const cleanData = () => {
    dispatch(setEditing(null));
    dispatch(setOrignTemplate(null));
    dispatch(setTemplate(null));
    dispatch(setSaveTemplateError(null));
  };

  const removeTemplate = (id) => {
    let _data = { ...currentPlacementTemplatePacket };
    let _placementTemplates = [..._data.PlacementTemplates];
    let _newPlacementTemplates = [];
    _placementTemplates.map((pt) => {
      let _pt = { ...pt };
      if (pt.Id !== id) {
        _newPlacementTemplates.push(_pt);
      }
    });
    _data.PlacementTemplates = _newPlacementTemplates;
    dispatch(setData(_data));
  };

  const getError = (field) => {
    if (isJsonValueEmpty(saveTemplateError)) return '';
    let err = { ...saveTemplateError };
    //check property of dict obj
    field = (field + '').toLocaleLowerCase();
    for (let key in err) {
      let k = key.toLocaleLowerCase();
      if (!isValueEmpty(key) && k.startsWith(field)) {
        let id = k.replace(field, '');
        if (isInt(id)) {
          if (template.Id + '' === id + '') {
            return err[key];
          }
        }
      }
    }
    return '';
  };

  const handleCancel = () => {
    removeUnsavedNewTemplate();
    switchViewMode();
    setTemplate({ ...selectedOrignTemplate });
  };

  const removeUnsavedNewTemplate = () => {
    let _data = { ...currentPlacementTemplatePacket };
    let _placementTemplates = [..._data.PlacementTemplates];
    let _newPlacementTemplates = [];
    let isNew = false;
    _placementTemplates.map((pt) => {
      let _pt = { ...pt };
      if (pt.Id === SENSOR_PLACEMENT_CONFIG.TPL_ID) {
        isNew = true;
      } else {
        _newPlacementTemplates.push(_pt);
      }
    });
    if (!isNew) return;
    _data.PlacementTemplates = _newPlacementTemplates;
    dispatch(setData(_data));
  };

  const preTotErrorCheck = (_selectedTemplate) => {
    let tots = _selectedTemplate.TemplateObservationTypes;
    let err = {};
    if (tots && tots.length === 1 && isValueEmpty(tots[0].ObservationTypeId)) {
      let v = [];
      v.push('Observation type is required.');
      err[totOtIdPrefix + tots[0].Id] = v;
      return err;
    }
    if (tots && tots.length > 1) {
      let emptyOtIds = [];
      let isFoundValued = false;
      tots.map((s, i) => {
        if (isValueEmpty(s.ObservationTypeId)) {
          emptyOtIds.push(s.Id);
        } else {
          isFoundValued = true;
        }
      });
      if (!isFoundValued && emptyOtIds.length > 0) {
        emptyOtIds.map((o, i) => {
          let v = [];
          v.push('Observation type is required.');
          err[totOtIdPrefix + o] = v;
        });
        return err;
      }
      if (!isJsonValueEmpty(err)) return err;
    }

    //1- check duplicated obervation type id
    let otIds = tots.map((item) => {
      return item.ObservationTypeId;
    });
    let foundErr = false;
    tots.map((s, i) => {
      let dups = [];
      otIds.map((idx) => {
        if (
          s.ObservationTypeId > 0 &&
          s.ObservationTypeId == idx &&
          !(
            _selectedTemplate.SensorTypeName === FROST_PROBE_CONFIG.FORST_PROBE_SENSOR_TYPE &&
            s.ObservationTypeName === FROST_PROBE_CONFIG.SUBSFC_TEMP
          ) &&
          !(
            _selectedTemplate.SensorTypeName === SNOW_POLE_CONFIG.SNOW_POLE_SENSOR_TYPE &&
            s.ObservationTypeName === SNOW_POLE_CONFIG.SNWPK_TEMP
          )
        )
          dups.push(idx);
      });

      if (dups && dups.length > 1) {
        let v = [];
        v.push('Duplicated observation type selected.');
        err[totOtIdPrefix + s.Id] = v;
        foundErr = true;
      }

      //2-check offsetelevationmillimetres
      if (
        isValueEmpty(s.OffsetElevationMillimetres) &&
        ((_selectedTemplate.SensorTypeName === FROST_PROBE_CONFIG.FORST_PROBE_SENSOR_TYPE &&
          s.ObservationTypeName === FROST_PROBE_CONFIG.SUBSFC_TEMP) ||
          (_selectedTemplate.SensorTypeName === SNOW_POLE_CONFIG.SNOW_POLE_SENSOR_TYPE &&
            s.ObservationTypeName === SNOW_POLE_CONFIG.SNWPK_TEMP))
      ) {
        let v = [];
        v.push('Offset value is empty.');
        err[totOffsetelevationmillimetresPrefix + s.Id] = v;
        foundErr = true;
      }
      if (
        !isValueEmpty(s.OffsetElevationMillimetres) &&
        FROST_PROBE_CONFIG.FORST_PROBE_SENSOR_TYPE === template.SensorTypeName
      ) {
        let v = [];
        let digits = 1;
        if (!isValidRelElevation(s.OffsetElevationMillimetres)) {
          v.push('invalid value. ');
          err[totOffsetelevationmillimetresPrefix + s.Id] = v;
          foundErr = true;
        } else if (!isValidRelElevationDigit(s.OffsetElevationMillimetres, digits)) {
          v.push(`Offset can only have ${digits} decimal place`);
          err[totOffsetelevationmillimetresPrefix + s.Id] = v;
          foundErr = true;
        }
        if (s.OffsetElevationMillimetres > 0) {
          v.push('Negative value required.');
          err[totOffsetelevationmillimetresPrefix + s.Id] = v;
          foundErr = true;
        }
      }
      if (
        !isValueEmpty(s.OffsetElevationMillimetres) &&
        SNOW_POLE_CONFIG.SNOW_POLE_SENSOR_TYPE === template.SensorTypeName
      ) {
        let v = [];
        let digits = 1;
        if (!isValidRelElevation(s.OffsetElevationMillimetres)) {
          v.push('invalid value. ');
          err[totOffsetelevationmillimetresPrefix + s.Id] = v;
          foundErr = true;
        } else if (!isValidRelElevationDigit(s.OffsetElevationMillimetres, digits)) {
          v.push(`Offset can only have ${digits} decimal place`);
          err[totOffsetelevationmillimetresPrefix + s.Id] = v;
          foundErr = true;
        }
        if (s.OffsetElevationMillimetres < 0) {
          v.push('Positive value required.');
          err[totOffsetelevationmillimetresPrefix + s.Id] = v;
          foundErr = true;
        }
      }
    });

    if (!foundErr) return null;
    return err;
  };
  const preTemplateErrorCheck = (totErrs) => {
    let psErrs = {};
    if (!isJsonValueEmpty(totErrs)) psErrs = totErrs;
    //0- check name
    if (isValueEmpty(selectedTemplate.Name)) {
      let vals = [];
      vals.push('Template name must be valued.');
      psErrs[tplNamePrefix + selectedTemplate.Id] = vals;
    }
    if (isJsonValueEmpty(psErrs)) return null;
    return psErrs;
  };
  const preErrorCheck = () => {
    let _selectedTemplate = { ...selectedTemplate };

    //0- check if at least one sot
    if (!_selectedTemplate.TemplateObservationTypes || _selectedTemplate.TemplateObservationTypes.length === 0) {
      let err = {};
      let v = [];
      v.push('No template observation type associated.');
      err['entity'] = v;
      toast.error('Could not save placement template ! No template observation type associated.', {
        autoClose: false,
      });
      return err;
    }
    let totErrs = preTotErrorCheck(_selectedTemplate);
    let errs = preTemplateErrorCheck(totErrs);
    return errs;
  };

  const onClickDeleteTemplate = () => {
    enableEdit();
    setShowDeleteTemplateConfirm(true);
  };
  const onClickShowCancel = () => {
    setShowCancelConfirm(true);
    setShowDeleteTemplateConfirm(false);
  };
  const deleteTemplate = () => {
    const id = template.Id;
    const url = REST_API.PLACEMENT_TEMPLATE + id;
    dispatch(setSaveTemplateError(null));
    dispatch(setSaveTemplateStatus(STATE_STATUS.LOADING));
    DeleteJson(url, {})
      .then(async (response) => {
        if (response.ok) {
          dispatch(setSaveTemplateStatus(STATE_STATUS.SUCCESS));
          removeTemplate(id);
          switchViewMode();
          toast.success('Placement template has been deleted');
        } else if (response.status === 412) {
          const errors = await response.json();
          if (errors) {
            dispatch(setSaveTemplateError(errors));
            toast.error('Could not save placement template ! ', {
              autoClose: false,
            });
          }
        }
      })
      .finally(() => {
        //setSubmitting(false);
      });
  };
  const handleTemplateSubmit = () => {
    dispatch(setSaveTemplateError(null));
    let errs = preErrorCheck();
    if (!isJsonValueEmpty(errs)) {
      dispatch(setSaveTemplateError(errs));
      return;
    }

    let _selectedTemplate = cleanTemplate();

    setSaveTemplateStatus(STATE_STATUS.LOADING);

    dispatch(setTemplate(_selectedTemplate));
    dispatch(saveTemplate());
  };

  /// remove empty tot row
  const cleanTemplate = () => {
    let _selectedTemplate = { ...selectedTemplate };
    let _templateObservationTypes = [..._selectedTemplate.TemplateObservationTypes];
    let _cleanTemplateObservationTypes = [];
    _templateObservationTypes.map((s) => {
      if (!isValueEmpty(s.ObservationTypeId) && s.ObservationTypeId !== 0) _cleanTemplateObservationTypes.push(s);
    });
    _selectedTemplate.TemplateObservationTypes = _cleanTemplateObservationTypes;
    return _selectedTemplate;
  };

  const switchCollapsed = () => {
    if (selectedTemplate && selectedTemplate.Id < 0 && isCurrentEditing() && isCollapsed) {
      setIsCollapsed(false);
      return false;
    }
    return isCollapsed;
  };
  const isNewTemplate = () => {
    let _selectedTemplate = { ...selectedTemplate };
    if (!_selectedTemplate || _selectedTemplate.Id < 0) return true;
    return false;
  };
  return (
    template && (
      <Card style={{ marginTop: '0.2rem', paddingTop: '0.2rem', paddingBottom: '0.2rem' }}>
        <Card.Body style={{ padding: '0.4rem' }}>
          <Row>
            <Col md={8}>
              <AwpIsExpanded
                style={{ display: 'inline-block', marginRight: '0.2rem' }}
                collapsed={switchCollapsed()}
                onClick={isExpanded}
              />
              {!isCurrentEditing() && template && (
                <div style={{ display: 'inline-block', ...txtFontSize }}>{template.Name}</div>
              )}
              {isCurrentEditing() && selectedTemplate && (
                <div style={{ display: 'inline-block' }}>
                  <Form.Control
                    onChange={updateTemplateName}
                    className="text-size90"
                    type="text"
                    maxLength="50"
                    value={isValueEmpty(selectedTemplate.Name) ? '' : selectedTemplate.Name}
                  />
                </div>
              )}
              {!isValueEmpty(getError(tplNamePrefix)) && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {getError(tplNamePrefix)}
                </Form.Control.Feedback>
              )}
            </Col>
            <Col md={4} className="d-flex flex-row-reverse" style={{ marginTop: '0.1rem' }}>
              {!isCurrentEditing() && (
                <>
                  <Authorize requires={[Permissions.WriteSensorType]}>
                    <Button variant="outline-primary" style={btnStyle} onClick={enableEdit} disabled={isOtherEditing()}>
                      Edit
                    </Button>
                  </Authorize>
                </>
              )}
              {isCurrentEditing() && (
                <>
                  <Authorize requires={[Permissions.WriteSensorType]}>
                    {!showDeleteTemplateConfirm && !showCancelConfirm && (
                      <>
                        {!isNewTemplate() && (
                          <Button variant="danger" style={btnStyle} onClick={onClickDeleteTemplate}>
                            Delete
                          </Button>
                        )}

                        <Button variant="outline-primary" style={btnStyle} onClick={onClickShowCancel}>
                          Cancel
                        </Button>

                        <Button variant="outline-primary" style={btnStyle} onClick={handleTemplateSubmit}>
                          Save
                        </Button>
                      </>
                    )}
                    {!showCancelConfirm && showDeleteTemplateConfirm && (
                      <AwpYesNoDialog
                        action={deleteTemplate}
                        setShowYesNoConfirm={setShowDeleteTemplateConfirm}
                        props={{ style: btnDelStyle }}
                      />
                    )}
                    {!showDeleteTemplateConfirm && showCancelConfirm && (
                      <AwpYesNoDialog
                        action={handleCancel}
                        setShowYesNoConfirm={setShowCancelConfirm}
                        msg={'Are you sure you want to cancel?'}
                        props={{ style: btnDelStyle }}
                      />
                    )}
                  </Authorize>
                </>
              )}
            </Col>
          </Row>
          {!isCollapsed && (
            <Card.Body style={sotOutline}>
              {template && SENSOR_PLACEMENT_CONFIG.MAPPINGS.includes(template.SensorTypeName) ? (
                <Row
                  className="font-weight-bold text-white"
                  style={{ marginRight: '-1rem', backgroundColor: '#494949' }}
                >
                  <Col style={sotTitleStyle} md={4}>
                    {'Observation Type'}
                  </Col>
                  <Col style={sotTitleStyle} md={4}>
                    {'Display Name Override'}
                  </Col>
                  <Col style={sotTitleStyle} md={2}>
                    {template.SensorTypeName === FROST_PROBE_CONFIG.FORST_PROBE_SENSOR_TYPE
                      ? 'Offset (-cm)'
                      : 'Offset (+cm)'}
                  </Col>
                  <Col style={sotTitleStyle} md={2}>
                    {''}
                  </Col>
                </Row>
              ) : (
                <Row
                  className="font-weight-bold text-white"
                  style={{ marginRight: '-1rem', backgroundColor: '#494949' }}
                >
                  <Col style={sotTitleStyle} md={4}>
                    {'Observation Type'}
                  </Col>
                  <Col style={sotTitleStyle} md={5}>
                    {'Display Name Override'}
                  </Col>
                  <Col style={sotTitleStyle} md={3}>
                    {''}
                  </Col>
                </Row>
              )}
              {isCurrentEditing() &&
                selectedTemplate &&
                selectedTemplate &&
                selectedTemplate.TemplateObservationTypes &&
                selectedTemplate.TemplateObservationTypes.length > 0 &&
                selectedTemplate.TemplateObservationTypes.map((tot, i) => {
                  if (SENSOR_PLACEMENT_CONFIG.MAPPINGS.includes(selectedTemplate.SensorTypeName)) {
                    return (
                      <div key={'tot_container_key_' + i + ''}>
                        {<TemplateObservationTypeEdit key={'tot_edit_key_' + i + ''} tot={tot} isMultiMode={true} />}
                      </div>
                    );
                  } else {
                    return (
                      <div key={'tot_container_key_' + i + ''}>
                        {<TemplateObservationTypeEdit key={'tot_edit_key_' + i + ''} tot={tot} isMultiMode={false} />}
                      </div>
                    );
                  }
                })}
              {!isCurrentEditing() &&
                template &&
                template.TemplateObservationTypes &&
                template.TemplateObservationTypes.map((tot, i) => {
                  return (
                    <div key={'tot_container_key_' + i + ''}>
                      {
                        <TemplateObservationTypeInfo
                          key={'tot_info_key_' + i + ''}
                          tot={tot}
                          isMultiMode={SENSOR_PLACEMENT_CONFIG.MAPPINGS.includes(template.SensorTypeName)}
                        />
                      }
                    </div>
                  );
                })}
            </Card.Body>
          )}
        </Card.Body>
      </Card>
    )
  );
};

PlacementTemplate.propTypes = {
  template: PropTypes.any.isRequired,
};

export default PlacementTemplate;
